// File: frontend/src/services/legalService.ts

import { API_BASE_URL } from "../constants";

// ── Document types (mirror of LegalDocType.java) ─────────────────────────────

export type LegalDocType = "TERMS" | "PRIVACY" | "COOKIE" | "RULES";

/** Public frontend routes, one per document type. */
export const LEGAL_ROUTES: Record<LegalDocType, string> = {
    TERMS:   "/kullanim-kosullari",
    PRIVACY: "/gizlilik-politikasi",
    COOKIE:  "/cerez-politikasi",
    RULES:   "/hamur-abi-kurallari",
};

/** Route where the user can revisit their cookie choice. */
export const COOKIE_PREFERENCES_ROUTE = "/cerez-tercihleri";

/**
 * Shape returned by GET /api/legal/{type}.
 * The body arrives already rendered — every {{setting:...}} / {{const:...}}
 * token has been resolved by the backend, so nothing is interpolated here.
 */
export interface LegalDocument {
    docType:        LegalDocType;
    language:       string;
    title:          string;
    body:           string;
    version:        string;
    effectiveDate:  string | null;
}

/**
 * Fetch one published legal document.
 *
 * Uses plain fetch rather than apiClient on purpose: these pages must work for
 * signed-out visitors, and apiClient's 401 handling would clear auth state on a
 * request that never needed a token in the first place.
 *
 * Returns null on 404 so the page can render its own "not available" state.
 */
export const getLegalDocument = async (
    docType: LegalDocType,
    language: string
): Promise<LegalDocument | null> => {
    const lang = language.startsWith("tr") ? "tr" : "en";
    const response = await fetch(
        `${API_BASE_URL}/api/legal/${docType.toLowerCase()}?lang=${lang}`
    );

    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Legal document request failed: ${response.status}`);

    return response.json();
};

/**
 * Current version string of each published document, keyed by type.
 * The registration form sends the TERMS version back with the registration
 * request so the recorded consent always points at a concrete text.
 */
export const getLegalVersions = async (
    language: string
): Promise<Partial<Record<LegalDocType, string>>> => {
    const lang = language.startsWith("tr") ? "tr" : "en";
    const response = await fetch(`${API_BASE_URL}/api/legal/versions?lang=${lang}`);

    if (!response.ok) return {};
    return response.json();
};